import './style.sass';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { Routes } from 'src/types/routes';
import { useModelWithArguments } from 'app/hooks/useViewModel';
import { TextInfoItem } from 'app/components/common/TextInfoitem';
import plusIcon from 'assets/img/icons/plusPurpleIcon.svg';
import { UrlItemWithControlls } from 'app/components/common/UrlItemWithControlls';
import { formateDate } from 'app/utils/formateDate';
import { numberWithCommas } from 'scripts/utils/numberWithCommas';
import { UrlCopyItem } from 'app/components/common/UrlCopyItem';
import { useBreakpointType } from 'app/services/breakpoints';
import editIcon from 'assets/img/icons/editIcon.svg';
import { CampaignInnerPageViewModel } from 'app/viewModels/pages/CampaignInnerPageViewModel';
import { UpdateCampaignInfo } from 'app/components/UpdateCampaignInfo';
import { UpdateCampaignPlaylists } from 'app/components/UpdateCampaignPlaylists';
import { UpdateCampaignSong } from 'app/components/UpdateCampaignSong';
import starImage from 'assets/img/icons/star.svg';
import { getSpotifyPlaylistId } from 'app/utils/getSpotifyPlaylistId';
import { CampaignSteps } from 'app/components/CampaignSteps';
import { CampaignStatusSelect } from 'app/components/CampaignStatusSelect';
import { CampaignUpdateStatusModal } from 'app/components/CampaignUpdateStatusModal';

export const CampaignInnerPage = observer(() => {
    const [searchParams] = useSearchParams();
    const campaignId = searchParams.get(Routes.Args.CampaignId);
    const model = useModelWithArguments(CampaignInnerPageViewModel, campaignId);
    const {
        campaignModel,
        mainInfoModalModel,
        songModalModel,
        addPlaylistModalModel,
        addPlaylist,
        deletePlaylist,
        onSubmitUpdateMainInfo,
        isAdmin,
        isLoading,
    } = model;

    const { campaign } = campaignModel;
    const { isDesktop, isMobile } = useBreakpointType();

    const ownerName = campaignModel.customerName || '-';
    const ownerEmail = campaignModel.customerEmail || '-';
    const purchaseDate = campaignModel.purchaseDate ? formateDate(campaignModel.purchaseDate) : '-';
    const orderNumber = `#${campaignModel.orderNumber}` || '#';
    const streams = campaignModel.streams ? numberWithCommas(campaignModel.streams) : '-';
    const campaignTier = campaignModel.plan?.toLowerCase() || '-';

    const artistName = campaignModel.authorName || '-';
    const assetName = campaignModel.assetName || '-';
    const releaseDate = campaignModel.releaseDate ? formateDate(campaignModel.releaseDate) : '-';
    const assetLink = campaignModel.assetLink;
    const playlists = campaignModel.playlists;
    const status = campaignModel.status;

    const onOpenMainInfoUpdateModal = () => {
        mainInfoModalModel.setTrue();
    };

    const onOpenAddPlaylistModal = () => {
        addPlaylistModalModel.setTrue();
    };

    const onOpenSongUpdateModal = () => {
        songModalModel.setTrue();
    };

    const playlistsIds = useMemo(() => playlists?.map(p => getSpotifyPlaylistId(p)), [playlists]);

    return (
        <div className="campaign-inner-page">
            {!isAdmin && status && (
                <div className="steps-block">
                    <CampaignSteps activeStep={status} />
                </div>
            )}

            <div className="main-block">
                <div className="main-block__header">
                    <h2 className="main-block__title title-h2 dark-violet">Campaign Information</h2>

                    {isAdmin &&
                        <>
                            {isDesktop
                                ? (
                                    <span
                                        className="main-block__edit p1-medium violet-text"
                                        onClick={onOpenMainInfoUpdateModal}
                                    >
                                        Edit information
                                    </span>
                                )
                                : (
                                    <div className="edit-btn" onClick={onOpenMainInfoUpdateModal}>
                                        <img src={editIcon} alt="Copy icon" />
                                    </div>
                                )
                            }

                            <UpdateCampaignInfo
                                isActiveModel={mainInfoModalModel}
                                campaignModel={campaignModel}
                                onSubmit={onSubmitUpdateMainInfo}
                                isLoading={isLoading}
                            />
                        </>
                    }
                </div>

                <div className="main-block__content">
                    <TextInfoItem
                        title="Name"
                        desc={ownerName}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Email"
                        desc={ownerEmail}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Purchase date"
                        desc={purchaseDate}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Order number"
                        desc={orderNumber}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Guaranteed Streams"
                        desc={streams}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Campaign tier"
                        desc={campaignTier}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet cap-text"
                    />
                    {isAdmin &&
                        <TextInfoItem
                            title="Status"
                            desc={
                                <CampaignStatusSelect
                                    currentStatus={status}
                                    onSelectStatus={campaignModel.openUpdateStatusModal}
                                    dropdownDirection={'right'}
                                />
                            }
                            titleClassName="p2-regular grey-1"
                            descClassName="p1-medium dark-violet cap-text"
                        />
                    }
                </div>
            </div>

            <div className="song-block">
                <div className="song-block__header">
                    <h2 className="song-block__title title-h2 dark-violet">Song</h2>
                    {isAdmin &&
                        <>
                            {isDesktop
                                ? <span className="main-block__edit p1-medium violet-text" onClick={onOpenSongUpdateModal}>
                                Edit information
                                </span>
                                : (
                                    <div className="edit-btn" onClick={onOpenSongUpdateModal}>
                                        <img src={editIcon} alt="Copy icon" />
                                    </div>
                                )
                            }

                            <UpdateCampaignSong model={model} />
                        </>
                    }
                </div>

                <div className="song-block__content">
                    <TextInfoItem
                        title="Artist name"
                        desc={artistName}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="Song name"
                        desc={assetName}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet ellipsis-text"
                    />

                    <TextInfoItem
                        title="Release date"
                        desc={releaseDate}
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                    />

                    <TextInfoItem
                        title="URL"
                        desc={ assetLink
                            ? (<UrlCopyItem
                                url={assetLink}
                                onCopy={isDesktop ? null : model.pushCopiedNotification}
                            />)
                            : '-'
                        }
                        titleClassName="p2-regular grey-1"
                        descClassName="p1-medium dark-violet"
                        className="song-url"
                    />
                </div>
            </div>

            <div className="playlists-block">
                <div className="playlists-block__header">
                    <h2 className="playlists-block__title title-h2 dark-violet">Playlists</h2>
                    {isAdmin &&
                        <>
                            {isMobile &&
                            <div className="edit-btn" onClick={onOpenAddPlaylistModal}>
                                <img src={editIcon} alt="Copy icon" />
                            </div>
                            }

                            <UpdateCampaignPlaylists
                                isActiveModel={addPlaylistModalModel}
                                onSubmit={addPlaylist}
                                isLoading={isLoading}
                            />
                        </>
                    }
                </div>

                {isAdmin
                    ? (
                        <ul className="playlists-block__list">
                            {!!playlists?.length && playlists?.map((playlist, id) => (
                                <li className="playlists-block__item" key={`playlist_${id}__${campaign.id}`}>
                                    <UrlItemWithControlls
                                        url={playlist}
                                        onDelete={() => deletePlaylist(playlist)}
                                        onCopy={isDesktop ? null : model.pushCopiedNotification}
                                        isLoading={isLoading}
                                    />
                                </li>
                            ))}

                            {isMobile && !playlists?.length && (
                                <div className="playlists-block__placeholder">
                                    <img src={starImage} />
                                    <h3 className="title-h3 dark-violet">We are still working on your campaign!</h3>
                                    <p className="p2-medium grey-1">Be sure to look out for emails and check back here for updates!</p>
                                </div>
                            )}

                            {!isMobile &&
                                <li className="playlists-block__item add-btn" onClick={onOpenAddPlaylistModal}>
                                    <img src={plusIcon} alt="Copy icon" />
                                    <span className="p1-medium violet-text">Add playlist</span>
                                </li>
                            }
                        </ul>
                    )
                    : (playlists?.length
                        ? (
                            <ul className="playlists-block__user-list">
                                {playlistsIds?.map((id, index) => (
                                    <li className="playlists-block__user-item" key={`playlist__${index}__${id}`}>
                                        <iframe
                                            style={{ borderRadius: '14px' }}
                                            src={`https://open.spotify.com/embed/playlist/${id}`}
                                            width="100%"
                                            height="100%"
                                            allowFullScreen
                                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                            loading="lazy"
                                        />
                                    </li>
                                ))}
                            </ul>
                        )
                        : (
                            <div className="playlists-block__placeholder">
                                <img src={starImage} />
                                <h3 className="title-h3 dark-violet">We are still working on your campaign!</h3>
                                <p className="p2-medium grey-1">Be sure to look out for emails and check back here for updates!</p>
                            </div>
                        )
                    )
                }
            </div>

            <CampaignUpdateStatusModal model={campaignModel}/>
        </div>
    );
});

